import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Avatar,
    Box,
    Divider,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { signUp, updateProfile } from '../../actions/auth';
import { useAuth } from '../../contexts/AuthContext';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';

const Account = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, isAuthenticated, generateUsername, generateAvatarUrl } = useAuth();
    
    const isNewUser = location.state?.isNewUser;
    const initialData = location.state?.initialData;
    const isGoogleUser = currentUser?.googleId;
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(isNewUser);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        bio: '',
        imageUrl: '',
        location: '',
        interests: '',
        password: '',
        confirmPassword: ''
    });

    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        if (!isAuthenticated && !isNewUser) {
            navigate('/auth');
        }
    }, [isAuthenticated, navigate, isNewUser]);

    useEffect(() => {
        if (isNewUser && initialData) {
            const username = generateUsername();
            const avatarUrl = generateAvatarUrl();
            
            setFormData({
                ...formData,
                ...initialData,
                username: username,
                imageUrl: avatarUrl
            });
        } else if (currentUser) {
            setFormData({
                name: currentUser.name || '',
                email: currentUser.email || '',
                bio: currentUser.bio || '',
                imageUrl: currentUser.imageUrl || '',
                location: currentUser.location || '',
                interests: currentUser.interests || '',
                username: currentUser.username,
                googleId: currentUser.googleId,
                password: '',
                confirmPassword: ''
            });
        }
    }, [currentUser, isNewUser, initialData]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (isNewUser) {
                // Validate required fields
                if (!formData.email || !formData.name) {
                    throw new Error('Name and email are required');
                }

                // Password validation only for non-Google users
                if (!formData.googleId) {
                    if (!formData.password || formData.password.length < 6) {
                        throw new Error('Password must be at least 6 characters long');
                    }
                    if (formData.password !== formData.confirmPassword) {
                        throw new Error('Passwords do not match');
                    }
                }

                const [firstName = '', lastName = ''] = formData.name.split(' ');
                
                const signupData = {
                    email: formData.email,
                    password: formData.googleId ? undefined : formData.password,
                    firstName,
                    lastName,
                    imageUrl: formData.imageUrl,
                    bio: formData.bio || '',
                    location: formData.location || '',
                    interests: formData.interests || '',
                    username: formData.username,
                    googleId: formData.googleId
                };

                
                await dispatch(signUp(signupData));
                
                setNotification({
                    open: true,
                    message: 'Account created successfully!',
                    severity: 'success'
                });
                
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 2000);
            } else {
                // Update existing user profile
                const result = await dispatch(updateProfile({
                    ...formData,
                    username: formData.username
                }));
                
                if (result?.error) {
                    throw new Error(result.error);
                }
                
                setEditMode(false);
                setNotification({
                    open: true,
                    message: 'Profile updated successfully!',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Submission error:', error);
            setNotification({
                open: true,
                message: error.message || 'Error creating account. Please try again.',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    if (saving) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
            <Paper 
                elevation={2}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    backgroundColor: '#ffffff'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: '#2d3748' }}>
                        {isNewUser ? 'Complete Your Profile' : 'Account Settings'}
                    </Typography>
                    {!isNewUser && (
                        <Button
                            startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                            variant={editMode ? "contained" : "outlined"}
                            onClick={editMode ? handleSubmit : () => setEditMode(true)}
                            color={editMode ? "primary" : "inherit"}
                            disabled={saving}
                        >
                            {editMode ? (saving ? 'Saving...' : 'Save Changes') : 'Edit Profile'}
                        </Button>
                    )}
                </Box>

                <Divider sx={{ mb: 4 }} />

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                        <Avatar
                            src={formData.imageUrl}
                            alt={formData.name || 'User Avatar'}
                            sx={{
                                width: 120,
                                height: 120,
                                mb: 2,
                                border: '4px solid #f0f2f5',
                                backgroundColor: '#f0f2f5'
                            }}
                            imgProps={{
                                style: {
                                    objectFit: 'contain'
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            name="username"
                            label="Username"
                            value={formData.username}
                            disabled={true}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText="Your unique generated username to keep your profile anonymous"
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    WebkitTextFillColor: '#2d3748',
                                    opacity: 1,
                                    letterSpacing: '0.5px',
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    textTransform: 'lowercase',
                                    padding: '14px',
                                    fontFamily: 'monospace'
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '1rem',
                                    position: 'relative',
                                    transform: 'none',
                                    marginBottom: '8px'
                                },
                                '& .MuiInputLabel-shrink': {
                                    transform: 'none'
                                },
                                '& .MuiFormHelperText-root': {
                                    marginLeft: '14px'
                                }
                            }}
                        />
                        <TextField
                            name="name"
                            label="Name"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={!editMode}
                            fullWidth
                        />
                        <TextField
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={!editMode || isGoogleUser}
                            fullWidth
                        />
                        <TextField
                            name="bio"
                            label="Bio"
                            value={formData.bio}
                            onChange={handleChange}
                            disabled={!editMode}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <TextField
                            name="location"
                            label="Location"
                            value={formData.location}
                            onChange={handleChange}
                            disabled={!editMode}
                            fullWidth
                        />
                        <TextField
                            name="interests"
                            label="Interests"
                            value={formData.interests}
                            onChange={handleChange}
                            disabled={!editMode}
                            fullWidth
                            helperText="Separate interests with commas"
                        />
                        {isNewUser && (
                            <>
                                <TextField
                                    name="password"
                                    label="Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                    error={!!passwordError}
                                    helperText="Password must be at least 6 characters long"
                                />
                                <TextField
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                    error={!!passwordError}
                                    helperText={passwordError}
                                />
                            </>
                        )}
                    </Box>

                    {isNewUser && (
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={saving}
                                onClick={handleSubmit}
                            >
                                {saving ? 'Creating Account...' : 'Create Account'}
                            </Button>
                        </Box>
                    )}
                </form>
            </Paper>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Account;
